/* eslint-disable no-underscore-dangle */
import { API } from '../../api';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Input,
  Modal,
  Popconfirm,
  Row,
  Spin,
  Table,
  Tabs,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { EditableCell } from '../editableTable/editableCell';
import { EditableRow } from '../editableTable/editableRow';
import {
  _getMappingData,
  createColumnHeaders,
  createSampleDataRows,
  downloadFiles,
  handleError,
} from '../../utils/util';
import { createJSONtoFHIRRequestBody } from '../editableTable/editableTable.util';
import { getMappingTableWithPrePostVals } from '../dataQA/mappingQA.util';
import { groupBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { withRouter } from 'react-router-dom';
import DataSourceCreate from './dataSourceCreate';
import EmptyData from './emptyData';
import PropTypes from 'prop-types';
import React from 'react';
import SampleDataTable from './sampleDataTable';
import ValidationMessages from '../validationMessages';

const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Title } = Typography;

class DataSourceContainer extends React.Component {
  static propTypes = {
    client: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  };

  state = {
    loading: true,
    uploading: false,
    sampleData: null,
    data: [],
    dataSourceSearchText: '',
    mappings: [],
    mappingsByResourceType: {},
    selected: { name: '' },
    sampleDataColumns: [],
    sampleDataRows: [],
    serverSideError: false,
    showModal: false,
    editNameModalVisible: false,
    editNameFile: null,
    editNameModalTitle: null,
    editNameLoading: false,
    newName: '',
    tableColumns: [
      {
        title: 'Data Sources',
        dataIndex: 'name',
        defaultSortOrder: 'ascend',
        sorter: (a, b) => a.name.localeCompare(b.name),
        editable: false,
      },
      {
        title: '',
        dataIndex: null,
        display: false,
        render: (_, record) => {
          const { data } = this.state;
          const buttonId = `archive-btn-${record.name}`;
          const editButtonId = `edit-btn-${record.name}`;
          return data.length > 0 ? (
            <>
              <Button
                id={editButtonId}
                onClick={() => this.showEditNameModal(record)}
                data-cy={`edit-data-source-name-${record.name}`}
              >
                <EditOutlined />
              </Button>
              <Popconfirm title="Are you sure?" onConfirm={() => this.handleArchive(record)}>
                <Button id={buttonId} style={{ marginLeft: '10px' }}>
                  X
                </Button>
              </Popconfirm>
            </>
          ) : null;
        },
      },
    ],
  };

  componentDidMount() {
    return this.getData();
  }

  showEditNameModal = record => {
    const modalTitle = (
      <Title level={5}>
        Rename <span className="file-name">{record.name}</span>
      </Title>
    );
    this.setState({
      editNameModalVisible: true,
      editNameFile: record,
      editNameModalTitle: modalTitle,
    });
  };

  closeModalEdit = () => this.setState({ editNameModalVisible: false, newName: '' });

  handleEditName = async () => {
    this.setState({ editNameLoading: true });

    if (this.state.newName.length === 0 || /^\s+$/.test(this.state.newName)) {
      notification.error({
        description: 'Please enter a file name.',
      });
      this.setState({ editNameLoading: false });
      return;
    }

    for (const file of this.state.data) {
      if (file.name === this.state.newName) {
        notification.error({
          description: `A file with the name "${this.state.newName}" already exists for this client.`,
        });
        this.setState({
          newName: '',
          editNameLoading: false,
        });
        return;
      }
    }

    try {
      const res = await API.put(
        `/file/${this.state.editNameFile.id || this.state.editNameFile.key}`,
        {
          name: this.state.newName,
        },
      );

      if (res.status === 200) {
        notification.success({
          message: 'Success',
          description: `Updated name (${res.status})`,
        });
      }

      this.getData();
      this.setState({
        editNameModalVisible: false,
        editNameLoading: false,
        newName: '',
      });
    } catch (err) {
      handleError(err);
    }
  };

  addDataSource = newDataSource => {
    const { data } = this.state;
    this.setState({
      data: [...data, newDataSource],
    });
  };

  getData = async () => {
    const { client } = this.props;
    this.updateLoadingState(true);
    let data = await API.get('/file', {
      params: {
        client_id: client.id,
        archived: false,
      },
    });
    data = data.data.files.map(func => {
      const fnc = func;
      fnc.key = uuidv4();
      return fnc;
    });
    // TODO mapping version toggle in UI
    const mappings = await _getMappingData(client.id, 'latest', '*');
    this.setState(
      {
        loading: false,
        data,
        mappings: mappings.data.mappings,
      },
      () => this.updateMappingsByResourceType(),
    );
  };

  /**
   * Updates state to contain map from pair of resource type + namespace
   * to all of the mappings for that resource type / namespace.
   * For example, the value stored in state will look like:
   * {
   *  Condition (default): [
   *    { mapping1 }, { mapping2 }, ...
   *  ],
   *  Encounter (default): [
   *    { mapping1 }, { mapping2 }, ...
   *  ],
   * }
   */
  updateMappingsByResourceType = () => {
    const { mappings, selected } = this.state;
    const fileMappings = mappings.filter(mapping => mapping.file === selected.name);
    const mappingsByResourceType = groupBy(
      fileMappings,
      item => `"${item.resourcetype} (${item.namespace})"`,
    );
    this.setState({ mappingsByResourceType });
  };

  /**
   * Archive the given File record.
   * @param {Object} record
   */
  handleArchive = async record => {
    const { data } = this.state;
    const dataSource = [...data];

    try {
      const filePutResponse = await API.put(`/file/${record.id}`, {
        archived: true,
      });
      notification.success({
        description: `Deleted (${filePutResponse.status})`,
        duration: 2,
      });
      this.setState({
        data: dataSource.filter(item => item.id !== record.id),
      });
    } catch (e) {
      handleError(e);
    }
  };

  /**
   * Retrieve sample data from S3 and update the component state to reflect it.
   */
  getSampleData = async () => {
    const { client } = this.props;
    const { selected } = this.state;
    let fileData;

    this.setState({ loading: true, serverSideError: false });

    // first get file ID
    try {
      fileData = await API.get('/file', {
        params: {
          client_id: client.id,
          name: selected.name,
          archived: false,
        },
      });
    } catch (err) {
      handleError(err);
      return;
    }

    if (fileData.data.files.length === 0) {
      this.setState({ loading: false });
      return;
    }

    const fileId = fileData.data.files[0].id;

    try {
      const fileGetResponse = await API.get(`/file/${fileId}/raw`);
      let responseSampleData = fileGetResponse.data.sampleData;

      // if data is a single object (i.e not an array) then
      // convert to an array of that single object
      if (!Array.isArray(responseSampleData)) responseSampleData = [responseSampleData];

      if (responseSampleData.length === 0) {
        this.setState({
          sampleDataColumns: [],
          sampleDataRows: [],
          loading: false,
        });
      } else {
        const sampleDataColumns = createColumnHeaders(responseSampleData);
        const sampleDataRows = createSampleDataRows(responseSampleData);
        this.setState({
          sampleDataColumns,
          sampleDataRows,
          sampleData: responseSampleData[0],
          loading: false,
        });
      }
    } catch {
      this.setState({ serverSideError: true, loading: false });
    }
  };

  onCreateDataSourceClick = () => {
    this.setState({
      showModal: true,
    });
  };

  /**
   * Iterate through all FHIR resource types associated with a single file extract
   * Create ZIP files for each resourceType and download the sample data, FHIR resource preview,
   * and a mapping document.
   * @returns {Promise<void>}
   *
   * EXPERIMENTAL - this function does not totally work
   */
  downloadSamples = async () => {
    const { client } = this.props;
    const { mappings, sampleData, selected } = this.state;

    let res;

    try {
      res = await _getMappingData(client.id, mappings[0].version, '*');
    } catch (err) {
      handleError(err);
      return;
    }

    const allMappings = res.data.mappings;
    let reqBody;

    try {
      reqBody = await createJSONtoFHIRRequestBody(
        allMappings,
        client.id,
        sampleData,
        false,
        selected.name,
      );
    } catch (err) {
      handleError(err);
      return;
    }

    let fhirPreviewRes;

    try {
      fhirPreviewRes = await API.post('/preview', reqBody);
    } catch (err) {
      handleError(err);
      return;
    }

    if (
      !!fhirPreviewRes &&
      !!fhirPreviewRes.data &&
      !!fhirPreviewRes.data._DATA &&
      fhirPreviewRes.data._DATA.length > 0
    ) {
      for (const fhirPreview of fhirPreviewRes.data._DATA) {
        const mappingsPrePost = getMappingTableWithPrePostVals(fhirPreview, allMappings);
        downloadFiles(
          sampleData,
          fhirPreview,
          mappingsPrePost,
          fhirPreview.resourceType,
          '',
          allMappings[0].version,
          selected.name,
          selected.name,
        );
      }
    }
  };

  closeModalNew = () => this.setState({ showModal: false });

  /**
   * Updates the loading state. This is its own function so
   * that child components can call it and cause state update.
   */
  updateLoadingState = val => {
    this.setState({ loading: val });
  };

  /**
   * Post sample data to S3 using the /file/FILE_ID/raw endpoint.
   * The FormData posted contains the following parameters:
   * - id: The File ID
   * - file: The uploaded file itself
   * - client_id: The client ID
   */
  postNewFile = async (newFile, fileId = null) => {
    const { client } = this.props;
    const { selected } = this.state;
    let fileIdVal = fileId;

    if (fileId === null) {
      fileIdVal = selected.id;
    }

    const formData = new FormData();
    formData.append('id', fileIdVal);
    formData.append('file', newFile);
    formData.append('client_id', client.id);

    try {
      const postFileResponse = await API.post(`/file/${selected.id}/raw`, formData);
      notification.success({
        description: `Added Sample Data (${postFileResponse.status})`,
        duration: 2,
      });
      this.getSampleData();
    } catch (err) {
      handleError(err);
    }
  };

  /**
   * Callback function for data source search / filter.
   */
  searchDataSources = e => {
    this.setState({ dataSourceSearchText: e.target.value });
  };

  render() {
    const { client } = this.props;
    const {
      loading,
      mappingsByResourceType,
      sampleData,
      sampleDataColumns,
      sampleDataRows,
      selected,
      serverSideError,
      showModal,
      tableColumns,
      uploading,
    } = this.state;

    const { dataSourceSearchText } = this.state;
    const dataSourceListDisplayed = this.state.data.filter(item => {
      return item.name.toLowerCase().includes(dataSourceSearchText.toLowerCase());
    });

    const rowSelection = {
      onSelect: record => {
        this.setState({ selected: record }, () => {
          this.getSampleData();
          this.updateMappingsByResourceType();
        });
      },
      getCheckboxProps: () => ({ disabled: false }),
    };

    const components = {
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    };
    const columns = tableColumns.map(col => {
      if (!col.editable) {
        return col;
      }

      return {
        ...col,
        onCell: record => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
        }),
      };
    });

    let contentShown = <>Select a data source to show sample data.</>;

    if (sampleDataRows.length > 0) {
      contentShown = (
        <SampleDataTable
          postNewFile={this.postNewFile}
          setFile={this.setFile}
          sampleDataColumns={sampleDataColumns}
          sampleDataRows={sampleDataRows}
          loading={loading}
          uploading={uploading}
          fileId={selected.id}
          getSampleData={this.getSampleData}
        />
      );
    }

    if (sampleDataRows.length === 0 && selected.name !== '') {
      contentShown = (
        <EmptyData
          client={client}
          selected={selected}
          getSampleData={this.getSampleData}
          updateLoadingState={this.updateLoadingState}
          postNewFile={this.postNewFile}
        />
      );
    }

    if (serverSideError) {
      contentShown = (
        <Alert
          message="Error"
          description="There was an error on the server. Please try again or contact us for more information."
          type="error"
          showIcon
        />
      );
    }

    return (
      <>
        <Modal
          title="Create New Mapping"
          visible={showModal}
          width={1000}
          onCancel={this.closeModalNew}
          destroyOnClose
          footer={null}
        >
          <DataSourceCreate
            addDataSource={this.addDataSource}
            client={client}
            closeModal={this.closeModalNew}
          />
        </Modal>
        <Modal
          title={this.state.editNameModalTitle}
          visible={this.state.editNameModalVisible}
          onOk={this.handleEditName}
          onCancel={this.closeModalEdit}
          confirmLoading={this.state.editNameLoading}
        >
          <Input
            addonBefore="Name:"
            value={this.state.newName}
            onChange={e => this.setState({ newName: e.target.value })}
            data-cy="input-new-data-source-name"
          />
        </Modal>
        <Spin spinning={loading}>
          <Row justify="start" align="top" gutter={[40, 16]}>
            <Col span={8}>
              <Button
                onClick={this.onCreateDataSourceClick}
                style={{ marginTop: 16, marginBottom: 16 }}
              >
                Create Data Source
              </Button>
              <Row style={{ marginBottom: '10px' }}>
                <Input
                  data-cy="search-datasources-input"
                  style={{ width: '350px' }}
                  addonBefore="Search:"
                  onChange={this.searchDataSources}
                />
              </Row>
              <Table
                components={components}
                rowSelection={{ type: 'radio', ...rowSelection }}
                columns={columns}
                scroll={{ x: 'max-content' }}
                dataSource={dataSourceListDisplayed}
                rowKey="id"
                size="small"
              />
            </Col>
            <Col span={16}>
              <Tabs onTabClick={this.onTabClick}>
                <TabPane tab="Data" key="1">
                  {contentShown}
                </TabPane>
                <TabPane tab="Analysis" key="2">
                  <Row gutter={[40, 16]}>
                    {!loading && (
                      <Col>
                        <Card
                          title={
                            <>
                              Mappings <small>(latest)</small>
                              <Button style={{ float: 'right' }} onClick={this.getData}>
                                Refresh
                              </Button>
                              {!!selected.name && (
                                <Tooltip title="EXPERIMENTAL: Download sample data, mappings, and example transformed resources for selected data source.">
                                  <Button
                                    style={{ float: 'right', marginRight: 7 }}
                                    onClick={this.downloadSamples}
                                  >
                                    Download
                                  </Button>
                                </Tooltip>
                              )}
                            </>
                          }
                        >
                          {!!selected.name && (
                            <>
                              <p>
                                File: <code style={{ color: 'blue' }}>{selected.name}</code> is
                                mapped to the following FHIR resources:
                              </p>
                              <Collapse>
                                {Object.keys(mappingsByResourceType).map(fileResourceType => {
                                  const mappings = mappingsByResourceType[fileResourceType];
                                  return (
                                    <Panel
                                      key={Object.keys(mappingsByResourceType).indexOf(
                                        fileResourceType,
                                      )}
                                      header={fileResourceType.replace('"', '').replace('"', '')}
                                    >
                                      {}
                                      {mappings.map(fileMapping => {
                                        const value =
                                          !!fileMapping && !!fileMapping.value
                                            ? fileMapping.value
                                            : '';

                                        if (value.includes('_.') || value.includes('@segment:')) {
                                          return (
                                            <>
                                              <p>
                                                <b>Path: </b>
                                                {fileMapping.path}
                                                <br />
                                                <b>Value: </b>
                                                <code className="code-green">
                                                  {fileMapping.value}
                                                </code>
                                              </p>
                                              <Divider />
                                            </>
                                          );
                                        }

                                        return null;
                                      })}
                                    </Panel>
                                  );
                                })}
                              </Collapse>
                            </>
                          )}
                        </Card>
                        <br />
                        <ValidationMessages
                          fhirErrors={{}}
                          sampleData={sampleData}
                          mappings={mappingsByResourceType}
                        />
                      </Col>
                    )}
                  </Row>
                </TabPane>
              </Tabs>
            </Col>
          </Row>
        </Spin>
      </>
    );
  }
}

export default withRouter(DataSourceContainer);
